import React, { useState, useEffect } from "react";
import axios from "axios";
import { MyDomain } from "../../App";
import { useAppContext } from "../../AppContext";
import { useTranslation } from "react-i18next";

export default function MPHomeShowImages() {
  const [mainImage, setMainImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoadError, setImageLoadError] = useState({});
  const { t } = useTranslation();

  const { AllData } = useAppContext();

  useEffect(() => {
    if (AllData?.barberData?.id) {
      fetchImages();
    }
  }, [AllData?.barberData?.id]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${MyDomain}Barbers2/GetAllImagesByBarberId/${AllData.barberData.id}`,
      );

      // Separate main image from gallery images
      setMainImage(response.data.mainImage || null);
      setGalleryImages(response.data.galleryImages || []);
    } catch (error) {
      console.error("Error fetching images:", error);
      setMainImage(null);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Combine main image and gallery for lightbox navigation
  const allImagesForLightbox = [
    ...(mainImage
      ? [{ id: mainImage.id, image: mainImage.image, isMain: true }]
      : []),
    ...galleryImages.map((img) => ({ ...img, isMain: false })),
  ];

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = "auto";
  };

  const navigateImage = (direction) => {
    const newIndex =
      direction === "next"
        ? (currentImageIndex + 1) % allImagesForLightbox.length
        : (currentImageIndex - 1 + allImagesForLightbox.length) %
          allImagesForLightbox.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(allImagesForLightbox[newIndex].image);
  };

  const handleImageError = (itemId) => {
    setImageLoadError((prev) => ({ ...prev, [itemId]: true }));
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedImage) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") navigateImage("prev");
      if (e.key === "ArrowRight") navigateImage("next");
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [selectedImage, currentImageIndex]);

  const styles = {
    galleryContainer: {
      width: "100%",
      padding: "0",
      margin: "0",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    galleryLayout: {
      display: "flex",
      gap: "0.5rem",
      padding: "0.5rem",
      alignItems: "flex-start",
    },
    mainImageSection: {
      flexShrink: 0,
      width: "200px",
      position: "relative",
    },
    mainImageContainer: {
      position: "relative",
      width: "100%",
      height: "200px",
      borderRadius: "12px",
      overflow: "hidden",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
      cursor: "pointer",
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    mainImageWrapper: {
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
    },
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    mainImageBadge: {
      position: "absolute",
      top: "0.5rem",
      left: "0.5rem",
      background: "rgba(255, 215, 0, 0.95)",
      color: "#1a1a1a",
      padding: "0.25rem 0.6rem",
      borderRadius: "20px",
      fontSize: "0.65rem",
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      display: "flex",
      alignItems: "center",
      gap: "0.3rem",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      zIndex: "2",
    },
    mainImageOverlay: {
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: "0",
      transition: "opacity 0.3s ease",
      zIndex: "1",
    },
    mainImageOverlayContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "0.3rem",
      color: "white",
      transform: "translateY(10px)",
      transition: "transform 0.3s ease",
    },
    mainImageOverlayText: {
      fontSize: "0.7rem",
      fontWeight: "600",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },
    gallerySection: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden", // Add this to contain the scroll
    },
    galleryScroll: {
      display: "flex",
      gap: "0.3rem",
      overflowX: "auto",
      overflowY: "hidden",
      padding: "0.2rem 0",
      scrollBehavior: "smooth",
      WebkitOverflowScrolling: "touch",
      // Add these:
      height: "120px", // Match the gallery item height
      alignItems: "center",
    },
    galleryItem: {
      position: "relative",
      flexShrink: 0,
      cursor: "pointer",
      borderRadius: "8px",
      overflow: "hidden",
      background: "#f5f5f5",
      transition:
        "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      width: "120px",
      height: "120px",
    },
    imageWrapper: {
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
    },
    galleryImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    imageError: {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f5f5f5",
      color: "#999",
    },
    galleryOverlay: {
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: "0",
      transition: "opacity 0.3s ease",
    },
    overlayContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "0.2rem",
      color: "white",
      transform: "translateY(10px)",
      transition: "transform 0.3s ease",
    },
    overlayText: {
      fontSize: "0.65rem",
      fontWeight: "600",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },
    loadingState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      gap: "0.75rem",
      padding: "1rem",
    },
    spinner: {
      width: "40px",
      height: "40px",
      border: "4px solid #f3f3f3",
      borderTop: "4px solid #667eea",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    loadingText: {
      fontSize: "0.9rem",
      color: "#666",
      fontWeight: "500",
      margin: "0",
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      padding: "2rem 1rem",
      textAlign: "center",
    },
    emptyIcon: {
      marginBottom: "1rem",
      color: "#cbd5e0",
      opacity: "0.8",
    },
    emptyTitle: {
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#2d3748",
      margin: "0 0 0.5rem 0",
    },
    emptyDescription: {
      fontSize: "0.9rem",
      color: "#718096",
      margin: "0",
      maxWidth: "300px",
    },
    lightbox: {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      background: "rgba(0, 0, 0, 0.95)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: "9999",
      backdropFilter: "blur(10px)",
    },
    lightboxContent: {
      position: "relative",
      maxWidth: "90vw",
      maxHeight: "90vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    lightboxImage: {
      maxWidth: "100%",
      maxHeight: "90vh",
      objectFit: "contain",
      borderRadius: "8px",
      boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
    },
    lightboxClose: {
      position: "fixed",
      top: "1rem",
      right: "1rem",
      width: "40px",
      height: "40px",
      background: "rgba(255, 255, 255, 0.1)",
      border: "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "50%",
      color: "white",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      zIndex: "10001",
      backdropFilter: "blur(10px)",
    },
    lightboxNav: {
      position: "fixed",
      top: "50%",
      transform: "translateY(-50%)",
      width: "40px",
      height: "40px",
      background: "rgba(255, 255, 255, 0.1)",
      border: "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "50%",
      color: "white",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      zIndex: "10001",
      backdropFilter: "blur(10px)",
    },
    lightboxNavPrev: {
      left: "1rem",
    },
    lightboxNavNext: {
      right: "1rem",
    },
    lightboxCounter: {
      position: "fixed",
      bottom: "1.5rem",
      left: "50%",
      transform: "translateX(-50%)",
      background: "rgba(0, 0, 0, 0.7)",
      color: "white",
      padding: "0.5rem 1.2rem",
      borderRadius: "25px",
      fontSize: "0.8rem",
      fontWeight: "600",
      zIndex: "10001",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
    },
  };

  if (loading) {
    return (
      <div style={styles.galleryContainer}>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={styles.loadingState}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>{t("Loading gallery...")}</p>
        </div>
      </div>
    );
  }

  if (!mainImage && (!galleryImages || galleryImages.length === 0)) {
    return (
      <div style={styles.galleryContainer}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <h3 style={styles.emptyTitle}>{t("No Images Yet")}</h3>
          <p style={styles.emptyDescription}>
            {t("Check back soon for our latest work")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    /* Hide scrollbar but keep functionality */
    .gallery-scroll::-webkit-scrollbar {
      height: 6px;
    }
    
    .gallery-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    
    .gallery-scroll::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    }
    
    .gallery-scroll::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.3);
    }

    /* Main Image Hover Effects */
    .main-image-container:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(102, 126, 234, 0.35);
    }
    
    .main-image-container:hover .main-image {
      transform: scale(1.08);
    }
    
    .main-image-container:hover .main-image-overlay {
      opacity: 1;
    }
    
    .main-image-container:hover .main-image-overlay-content {
      transform: translateY(0);
    }

    /* Gallery Item Hover Effects */
    .gallery-item:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.18);
    }
    
    .gallery-item:hover .gallery-image {
      transform: scale(1.08);
    }
    
    .gallery-item:hover .gallery-overlay {
      opacity: 1;
    }
    
    .gallery-item:hover .overlay-content {
      transform: translateY(0);
    }
    
    /* Lightbox Button Hover Effects */
    .lightbox-close:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.4);
      transform: rotate(90deg);
    }
    
    .lightbox-nav:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.4);
      transform: translateY(-50%) scale(1.15);
    }
    
    /* Responsive Design */
    @media (max-width: 768px) {
      .gallery-layout {
        flex-direction: column !important;
        gap: 0.75rem !important;
      }
      
      .main-image-section {
        width: 100% !important;
      }
      
      .main-image-container {
        height: 180px !important;
      }
      
      /* Mobile Scroll Fix */
      .gallery-section {
        width: 100% !important;
        overflow: hidden !important;
      }
      
      .gallery-scroll {
        display: flex !important;
        flex-direction: row !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        -webkit-overflow-scrolling: touch !important;
      }
      
      .gallery-item {
        width: 110px !important;
        height: 110px !important;
        flex-shrink: 0 !important;
      }
      
      .lightbox-close,
      .lightbox-nav {
        width: 36px !important;
        height: 36px !important;
      }
      
      .lightbox-counter {
        bottom: 1rem !important;
        padding: 0.4rem 1rem !important;
        font-size: 0.75rem !important;
      }
    }
    
    @media (max-width: 480px) {
      .main-image-container {
        height: 160px !important;
        border-radius: 10px !important;
      }
      
      /* Small Mobile Scroll Fix */
      .gallery-scroll {
        gap: 0.4rem !important;
      }
      
      .gallery-item {
        width: 100px !important;
        height: 100px !important;
        flex-shrink: 0 !important;
      }
      
      .main-image-badge {
        font-size: 0.6rem !important;
        padding: 0.2rem 0.5rem !important;
      }
    }
  `}
      </style>

      <div style={styles.galleryContainer}>
        <div style={styles.galleryLayout} className="gallery-layout">
          {/* Main Image Section */}
          {mainImage && (
            <div style={styles.mainImageSection} className="main-image-section">
              <div
                style={styles.mainImageContainer}
                className="main-image-container"
                onClick={() => openLightbox(mainImage.image, 0)}
              >
                {/* Main Image Badge */}
                <div style={styles.mainImageBadge}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span>{t("Featured")}</span>
                </div>

                <div style={styles.mainImageWrapper}>
                  {!imageLoadError[mainImage.id] ? (
                    <img
                      src={mainImage.image}
                      alt={t("Main gallery image")}
                      style={styles.mainImage}
                      className="main-image"
                      loading="eager"
                      onError={() => handleImageError(mainImage.id)}
                    />
                  ) : (
                    <div style={styles.imageError}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect
                          x="3"
                          y="3"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <line x1="9" y1="9" x2="15" y2="15" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                      </svg>
                    </div>
                  )}

                  <div
                    style={styles.mainImageOverlay}
                    className="main-image-overlay"
                  >
                    <div
                      style={styles.mainImageOverlayContent}
                      className="main-image-overlay-content"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                        <line x1="11" y1="8" x2="11" y2="14" />
                        <line x1="8" y1="11" x2="14" y2="11" />
                      </svg>
                      <span style={styles.mainImageOverlayText}>
                        {t("View")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Gallery Images Section */}
          {galleryImages && galleryImages.length > 0 && (
            <div style={styles.gallerySection} className="gallery-section">
              <div style={styles.galleryScroll} className="gallery-scroll">
                {galleryImages.map((item, index) => {
                  // Adjust index for lightbox (account for main image being first)
                  const lightboxIndex = mainImage ? index + 1 : index;

                  return (
                    <div
                      key={item.id}
                      style={styles.galleryItem}
                      className="gallery-item"
                      onClick={() => openLightbox(item.image, lightboxIndex)}
                    >
                      <div style={styles.imageWrapper}>
                        {!imageLoadError[item.id] ? (
                          <img
                            src={item.image}
                            alt={`${t("Gallery image")} ${index + 1}`}
                            style={styles.galleryImage}
                            className="gallery-image"
                            loading="lazy"
                            onError={() => handleImageError(item.id)}
                          />
                        ) : (
                          <div style={styles.imageError}>
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect
                                x="3"
                                y="3"
                                width="18"
                                height="18"
                                rx="2"
                                ry="2"
                              />
                              <line x1="9" y1="9" x2="15" y2="15" />
                              <line x1="15" y1="9" x2="9" y2="15" />
                            </svg>
                          </div>
                        )}
                        <div
                          style={styles.galleryOverlay}
                          className="gallery-overlay"
                        >
                          <div
                            style={styles.overlayContent}
                            className="overlay-content"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="11" cy="11" r="8" />
                              <path d="m21 21-4.35-4.35" />
                              <line x1="11" y1="8" x2="11" y2="14" />
                              <line x1="8" y1="11" x2="14" y2="11" />
                            </svg>
                            <span style={styles.overlayText}>{t("View")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Lightbox Modal */}
      {selectedImage && (
        <div style={styles.lightbox} onClick={closeLightbox}>
          <button
            style={styles.lightboxClose}
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label={t("Close")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div
            style={styles.lightboxContent}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt={`${t("Gallery image")} ${currentImageIndex + 1}`}
              style={styles.lightboxImage}
            />

            <div style={styles.lightboxCounter}>
              {currentImageIndex + 1} / {allImagesForLightbox.length}
            </div>
          </div>

          {allImagesForLightbox.length > 1 && (
            <>
              <button
                style={{ ...styles.lightboxNav, ...styles.lightboxNavPrev }}
                className="lightbox-nav lightbox-nav-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage("prev");
                }}
                aria-label={t("Previous image")}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>

              <button
                style={{ ...styles.lightboxNav, ...styles.lightboxNavNext }}
                className="lightbox-nav lightbox-nav-next"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage("next");
                }}
                aria-label={t("Next image")}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
